import React, { useState, useRef, useEffect } from 'react';
import { Select, SelectContent, SelectTrigger, SelectValue, SelectItem } from './select';
import { Input } from './input';
import { Button } from './button';
import { Plus } from 'lucide-react';

interface SearchableSelectProps {
  value: string | undefined;
  onValueChange: (value: string) => void;
  placeholder: string;
  searchPlaceholder: string;
  /** `name` is shown in the list; `searchText` (optional) is used only for filtering (e.g. include email). */
  options: Array<{ id: string | number; name: string; searchText?: string }>;
  onAddNew?: () => void;
  addNewText?: string;
  showAddButton?: boolean;
  icon?: React.ReactNode;
  disabled?: boolean;
  loading?: boolean;
  loadingText?: string;
  className?: string;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onValueChange,
  placeholder,
  searchPlaceholder,
  options,
  onAddNew,
  addNewText = "Add New",
  showAddButton = true,
  icon,
  disabled = false,
  loading = false,
  loadingText = "Loading...",
  className = ""
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selectTriggerRef = useRef<HTMLButtonElement>(null);
  const optionsListRef = useRef<HTMLDivElement>(null);

  // Filter options based on search term, but always include the currently selected option
  const filteredOptions = options.filter(option => {
    const q = searchTerm.toLowerCase();
    const haystack = (option.searchText ?? option.name ?? '').toLowerCase();
    const matchesSearch = !q || haystack.includes(q);
    const isCurrentlySelected = value && option.name === value;
    return matchesSearch || isCurrentlySelected;
  });

  // Handle option selection
  const handleOptionSelect = (selectedValue: string) => {
    const selectedOption = options.find(option => option.name === selectedValue);
    if (selectedOption) {
      onValueChange(selectedValue);
      setSearchTerm(''); // Clear search when value is selected
      setIsOpen(false);
    }
  };

  // Handle search input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    // If user starts typing and there's a selected value, clear the selection
    if (value && e.target.value !== value) {
      // This allows the user to search for a different option
    }
  };

  // Handle search input focus
  const handleSearchFocus = () => {
    // Prevent the select from closing when clicking on search input
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Do not close on blur — Dialog focus trap briefly moves focus; outside-click
  // handling on Select root already closes when clicking outside the portal.
  const handleSearchBlur = (_e: React.FocusEvent<HTMLInputElement>) => {};

  // Handle focus on the dropdown container to maintain focus
  const handleDropdownFocus = () => {
    // If no options are available, keep focus on search input
    if (filteredOptions.length === 0 && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Handle search input key events
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();

    // Handle Enter key to select first filtered option
    if (e.key === 'Enter' && filteredOptions.length > 0) {
      e.preventDefault();
      handleOptionSelect(filteredOptions[0].name);
    }

    // Handle Escape key to close dropdown
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
      selectTriggerRef.current?.focus();
    }

    // Handle Tab key to maintain focus when no options available
    if (e.key === 'Tab' && filteredOptions.length === 0) {
      e.preventDefault();
      // Keep focus on search input when no options are available
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  };

  // Handle mouse/pointer on search input so Dialog focus trap + select
  // outside-click logic do not steal focus or close the menu.
  const handleSearchPointerDown = (e: React.PointerEvent<HTMLInputElement>) => {
    e.stopPropagation();
  };

  const handleSearchClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
  };

  const handleSearchMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
  };

  const handleDropdownWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const optionsList = optionsListRef.current;
    if (!optionsList || optionsList.scrollHeight <= optionsList.clientHeight) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    optionsList.scrollTop += e.deltaY;
  };

  // Handle add new button click
  const handleAddNew = () => {
    onAddNew?.();
    setIsOpen(false);
    setSearchTerm('');
  };

  // Focus search input when dropdown opens
  useEffect(() => {
    if (!isOpen) return;
    const timer = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [isOpen]);

  // Clear search when select closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  // Show search whenever there are enough source options (not filtered count),
  // so typing never unmounts the search box mid-query.
  const showSearch = options.length > 5;

  return (
    <div className={`relative ${className}`}>
      {icon}
      <Select
        value={value}
        onValueChange={handleOptionSelect}
        disabled={disabled || loading}
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <SelectTrigger
          ref={selectTriggerRef}
          className={` text-text-grey text-left ${icon ? 'pl-9' : 'pl-3'}`}
        >
          <SelectValue className='placeholder:text-heritage-grey' placeholder={loading ? loadingText : <span className='text-heritage-grey'>{placeholder}</span>} />
        </SelectTrigger>
        <SelectContent className="max-h-[min(20rem,80vh)] overflow-hidden flex flex-col">
          {loading ? (
            <div className="p-2 text-center text-heritage-grey">
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-text-grey"></div>
                <span>{loadingText}</span>
              </div>
            </div>
          ) : (
            <div className="flex min-h-0 flex-col" onWheel={handleDropdownWheel}>
              {/* Search Input — use source options length so search stays mounted while filtering */}
              {showSearch && (
              <div
                className="p-2 border-b border-[#E2E5EA] flex-shrink-0 h-12"
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <Input
                  ref={searchInputRef}
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                  onKeyDown={handleSearchKeyDown}
                  onMouseDown={handleSearchMouseDown}
                  onPointerDown={handleSearchPointerDown}
                  onClick={handleSearchClick}
                  className="h-8 text-[13px] placeholder:text-heritage-grey"
                  autoComplete="off"
                  autoFocus
                />
              </div>
              )}

              {/* Options List – scrollable, contained inside dropdown */}
              <div
                ref={optionsListRef}
                className="flex-1 overflow-y-auto min-h-0 max-h-[12rem] overscroll-contain"
                onFocus={handleDropdownFocus}
                onWheel={(e) => e.stopPropagation()}
              >
                {filteredOptions.length > 0 ? (
                  filteredOptions.map(option => {
                    const isSelected = value && option.name === value;
                    return (
                      <SelectItem
                        key={option.id}
                        value={option.name}
                        className={isSelected ? "bg-[rgba(0,39,63,0.06)] text-blueprint-blue font-medium" : ""}
                      >
                        {option.name}
                      </SelectItem>
                    );
                  })
                ) : (
                  <div className="p-2 text-center text-heritage-grey text-[13px]">
                    {searchTerm ? 'No results found' : 'No options available'}
                  </div>
                )}
              </div>

              {/* Add New Button - Conditionally rendered */}
              {showAddButton && onAddNew && (
                <div className="border-t border-[#E2E5EA] p-1 flex-shrink-0 h-10 flex items-center justify-center">
                  <Button
                    className="flex items-center text-[13px] hover:bg-page-bg text-hero-red h-8 w-full"
                    variant="ghost"
                    onClick={handleAddNew}
                    type="button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {addNewText}
                  </Button>
                </div>
              )}
            </div>
          )}
        </SelectContent>
      </Select>
    </div>
  );
};
